import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getSortedRowModel,
  ExpandedState,
  SortingState,
  Row,
  ColumnDef,
  ColumnPinningState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useMemo, useState, useCallback, Fragment, useRef } from 'react';
import {
  DataTableSkeleton,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Tile,
  InlineNotification,
  Tag,
} from '@carbon/react';
import { ChevronRight, ChevronDown } from '@carbon/react/icons';
import { DndContext, DragMoveEvent, closestCenter } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import NoDataSvg from 'images/srp-illustration-data.svg';
import { TableActions } from 'components/table/table-actions';
import { useColumnFilter } from 'lib/hooks/useColumnFilter';
import { useColumnState } from 'lib/hooks/useColumnState';
import usePinnedColumnReordering from 'lib/hooks/usePinnedColumnReordering';
import { ColumnFilter } from 'components/table/column-filter';
import { HiddenColumns } from 'components/table/hidden-columns';
import { MIN_WIDTH } from 'components/table/column-actions';
import { EmptyState } from 'components/empty-state';
import {
  DragHeader,
  getPinningStyles,
  getInitialColumnOrder,
  handleDragEnd,
  handleDragMove,
} from 'components/table/draggable';
import { DECIMAL_FORMATTER } from 'lib/ui';
import { format8, formatRange, formatWholeDollarCurrency } from 'utils/number';
import plur from 'plur';
import styles from './crm-tracts-table.module.scss';
import {
  CRMTractsResponse,
  CRMTractsAndSubtractsResult,
  CRMSubtractResponse,
} from 'types/api-responses';
import {
  CELL_BASE_ROW_HEIGHT,
  CELL_HEIGHT,
  CELL_MARGIN,
  CELL_PADDING,
  formatTitleRiskValue,
} from 'pages/crm-tracts-subtracts/utils';
import { Date } from 'components/date';
import { Link } from 'components/link';
import { TARGET_NPV_SOURCE } from 'components/target-npv';

const columnHelper = createColumnHelper<CRMTractsResponse>();

const TARGET_NOTE = {
  infoTooltip: `${TARGET_NPV_SOURCE}.`,
};

const getColumnStyle = (size: number) => ({
  width: `${size}px`,
  minWidth: `${size}px`,
  maxWidth: `${size}px`,
});

const getExpanderColumn = (): ColumnDef<CRMTractsResponse, unknown> => ({
  id: 'expander',
  header: () => null,
  cell: ({ row }: { row: Row<CRMTractsResponse> }) => {
    const canExpand = (row.original.subtracts?.length ?? 0) > 0;
    const isExpanded = row.getIsExpanded();
    return canExpand ? (
      <>
        {isExpanded ? (
          <ChevronDown
            onClick={(e) => {
              e.stopPropagation();
              row.getToggleExpandedHandler()();
            }}
            cursor={'pointer'}
          />
        ) : (
          <ChevronRight
            onClick={(e) => {
              e.stopPropagation();
              row.getToggleExpandedHandler()();
            }}
            cursor={'pointer'}
          />
        )}
      </>
    ) : null;
  },
  size: 50,
  enableColumnFilter: false,
  enableGlobalFilter: false,
  meta: {
    toggleColumnFilter: false,
    isColumnExportable: false,
  },
});

// Neither endpoint that feeds this table (the package's and the entity's) sends a subtract an
// instrument type or a depth.
type SubtractRow = Omit<
  CRMSubtractResponse,
  'instrumentType' | 'startDepth' | 'endDepth'
>;
const subtractColumnHelper = createColumnHelper<SubtractRow>();

// Subtract table for expanded rows. A subtract has no depth of its own in Insights, so each row
// shows its Tract's depth range, formatted by the caller as the Tract column formats it.
const SubtractTable = ({
  subtractRows,
  depthRange,
}: {
  subtractRows: SubtractRow[];
  depthRange: string;
}) => {
  const columns = useMemo(
    () => [
      subtractColumnHelper.accessor('name', {
        header: 'Name',
        size: 200,
        cell: (info) => {
          const value = info.getValue();
          const salesforceId = info.row.original?.salesforceId;
          if (!value) return '--';
          // Subtract Name links to the full-page CRM Tract Wizard edit
          // route in a new tab. The wizard accepts either a tract or
          // subtract salesforce id at `/crm/tracts_and_subtracts/:id/edit`
          // (legacy `:tract_id` param name, polymorphic in practice).
          // Fall back to inert text when the row has no salesforce id.
          if (!salesforceId) return value;
          return (
            <Link
              to={`/crm/tracts_and_subtracts/${encodeURIComponent(
                salesforceId
              )}/edit`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.nameLink}
              onClick={(e) => e.stopPropagation()}
            >
              {value}
            </Link>
          );
        },
      }),
      subtractColumnHelper.accessor('netAcres', {
        header: 'Net Acres',
        size: 130,
        cell: (i) => format8(i.getValue()),
      }),
      subtractColumnHelper.accessor('grossAcres', {
        header: 'Gross Acres',
        size: 130,
        cell: (i) => format8(i.getValue()),
      }),
      subtractColumnHelper.display({
        id: 'depthRange',
        header: 'Depth Range',
        size: 120,
        cell: () => depthRange,
      }),
      subtractColumnHelper.accessor('titleQuality', {
        header: 'Title Quality',
        size: 130,
        cell: (i) => formatTitleRiskValue(i.getValue()),
      }),
      subtractColumnHelper.accessor('titleRiskFactor', {
        header: 'Title Risk Factor',
        size: 150,
        cell: (i) => formatTitleRiskValue(i.getValue()),
      }),
    ],
    [depthRange]
  );

  const subtractTable = useReactTable({
    data: subtractRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalWidth = columns.reduce((sum, col) => sum + (col.size || 200), 0);

  return (
    <div
      className={styles.subtractTableWrapper}
      style={{ width: `${totalWidth}px` }}
    >
      <Table>
        <TableHead>
          {subtractTable.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeader
                  key={header.id}
                  style={getColumnStyle(header.column.getSize())}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHeader>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {subtractTable.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  style={getColumnStyle(cell.column.getSize())}
                >
                  <div style={{ padding: '12px 0.75rem' }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export interface CRMTractsTableProps {
  isLoading: boolean;
  error?: string;
  data?: CRMTractsAndSubtractsResult;
  tableId?: string;
  showTableActions?: boolean;
}

export const CRMTractsTable = ({
  isLoading,
  error,
  data,
  tableId: tableIdProp,
  showTableActions = true,
}: CRMTractsTableProps) => {
  const tableId = tableIdProp ?? 'crm-tracts-table';
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [sorting, setSorting] = useState<SortingState>([]);

  // TractWizard modal removed in favor of new-tab links to the full-page
  // wizard at `/crm/tracts_and_subtracts/:salesforce_id/edit` (see Name
  // cells below). Tract enums, modal state, and handleTractClick all dropped.

  const {
    columnFilters,
    setColumnFilters,
    isColumnFilterActive,
    setIsColumnFilterActive,
  } = useColumnFilter(tableId);

  const resetCustomizations = useCallback(() => {
    setColumnFilters([]);
    setIsColumnFilterActive(false);
  }, [setColumnFilters, setIsColumnFilterActive]);

  const rows: CRMTractsResponse[] = useMemo(() => {
    if (data?.results) {
      return data.results;
    }
    return [];
  }, [data]);

  const columns = useMemo(
    () => [
      getExpanderColumn(),
      columnHelper.accessor('name', {
        header: 'Name',
        size: 200,
        cell: (info) => {
          const value = info.getValue();
          const salesforceId = info.row.original?.salesforceId;
          if (!value) return '--';
          // Tract Name links to the full-page CRM Tract Wizard edit route
          // (`/crm/tracts_and_subtracts/:id/edit`) in a new tab. The route
          // accepts either a tract or subtract salesforce id (legacy
          // `:tract_id` param name, polymorphic in practice).
          if (!salesforceId) return value;
          return (
            <Link
              to={`/crm/tracts_and_subtracts/${encodeURIComponent(
                salesforceId
              )}/edit`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.nameLink}
              onClick={(e) => e.stopPropagation()}
            >
              {value}
            </Link>
          );
        },
      }),
      columnHelper.accessor(
        (row) => row.entities?.map((entity) => entity.name).join(', '),
        {
          id: 'entities',
          header: 'Insights Entity',
          size: 150,
          cell: (info) => info.getValue() || '--',
        }
      ),
      columnHelper.accessor((row) => row.account?.name, {
        id: 'crmAccount',
        header: 'CRM Account',
        size: 150,
        cell: (info) => info.getValue() || '--',
      }),
      columnHelper.accessor('surveyArea', {
        header: 'Survey Area',
        size: 120,
        cell: (i) => i.getValue() || '--',
      }),
      columnHelper.accessor('quarterCall', {
        header: 'Quarter Call',
        size: 120,
        cell: (i) => i.getValue() || '--',
      }),
      columnHelper.accessor('netAcres', {
        header: 'Net Acres',
        size: 130,
        cell: (i) => format8(i.getValue()),
      }),
      columnHelper.accessor('netAcreageUnits', {
        header: 'Net Acreage Units',
        size: 140,
        cell: (i) => i.getValue() || '--',
      }),
      columnHelper.accessor('grossAcres', {
        header: 'Gross Acres',
        size: 130,
        cell: (i) => format8(i.getValue()),
      }),
      columnHelper.accessor('startDepth', {
        header: 'Depth Range',
        size: 120,
        cell: (i) =>
          formatRange({
            start: i.row.original.startDepth,
            end: i.row.original.endDepth,
          }),
      }),
      columnHelper.accessor('instrumentType', {
        header: 'Instrument Type',
        size: 130,
        cell: (i) => i.getValue() || '--',
      }),
      // Same cell as the CRM Tract Library's column, reading the same indicator the package tract
      // payload already carries. Display-only: the package tract query has no vesting sort or
      // filter target, and offering one here would 500 on an unknown field.
      columnHelper.accessor('vestingDocument', {
        header: 'Vesting Document',
        size: 150,
        enableSorting: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        cell: (i) => {
          const vesting = i.getValue();
          // A dash, not blank: most tracts never came through an acquisition and can never have a
          // deed, so "nothing here" is the ordinary answer rather than missing data.
          if (!vesting) return '--';

          return (
            <Link to={vesting.documentHref}>
              <Tag type={vesting.verified ? 'green' : 'magenta'}>
                {vesting.verified ? 'Verified' : 'Unverified'}
              </Tag>
            </Link>
          );
        },
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created Date',
        size: 160,
        cell: (i) => {
          const value = i.getValue();
          return value ? (
            <Date date={value} format="M/DD/YYYY h:mm A" showTime />
          ) : (
            '--'
          );
        },
      }),
      columnHelper.accessor('updatedAt', {
        header: 'Updated Date',
        size: 160,
        cell: (i) => {
          const value = i.getValue();
          return value ? (
            <Date date={value} format="M/DD/YYYY h:mm A" showTime />
          ) : (
            '--'
          );
        },
      }),
      // Per-row pricing columns (Eval 3.0). Read directly from the API —
      // no mapper layer in this code path.
      // Display-only: BE allowlist for sort/filter excludes these computed
      // fields, so client-side sort/filter is also disabled to keep parity.
      columnHelper.accessor('targetNpvExcluded', {
        header: 'Target - Broker',
        size: 160,
        meta: TARGET_NOTE,
        cell: (i) => formatWholeDollarCurrency(i.getValue()),
        enableSorting: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
      }),
      columnHelper.accessor('maxNpvExcluded', {
        header: 'Max - Broker',
        size: 160,
        cell: (i) => formatWholeDollarCurrency(i.getValue()),
        enableSorting: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
      }),
      columnHelper.accessor('targetNpvIncluded', {
        header: 'Target - SIM',
        size: 160,
        meta: TARGET_NOTE,
        cell: (i) => formatWholeDollarCurrency(i.getValue()),
        enableSorting: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
      }),
      columnHelper.accessor('maxNpvIncluded', {
        header: 'Max - SIM',
        size: 160,
        cell: (i) => formatWholeDollarCurrency(i.getValue()),
        enableSorting: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
      }),
      columnHelper.accessor('titleQuality', {
        header: 'Title Quality',
        size: 130,
        cell: (i) => formatTitleRiskValue(i.getValue()),
      }),
      columnHelper.accessor('titleRiskFactor', {
        header: 'Title Risk Factor',
        size: 150,
        cell: (i) => formatTitleRiskValue(i.getValue()),
      }),
    ],
    []
  );

  // Column ordering and pinning state
  const initialColumnPinning: ColumnPinningState = {
    left: ['expander', 'name'],
  };

  const initialColumnOrder = useMemo(
    () => getInitialColumnOrder(columns),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { columnOrder, setColumnOrder, columnPinning, setColumnPinning } =
    useColumnState({
      tableId,
      initialColumnOrder,
      initialColumnPinning,
    });

  const hoveredColumnIdRef = useRef<string | null>(null);

  usePinnedColumnReordering(columnPinning, columnOrder, setColumnOrder);

  const onDragMove = useCallback(
    (e: DragMoveEvent) => {
      handleDragMove(e, hoveredColumnIdRef);
    },
    [hoveredColumnIdRef]
  );

  const parentRef = useRef<HTMLDivElement>(null);

  const table = useReactTable({
    data: rows,
    columns,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: MIN_WIDTH,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: (row) => {
      return (row.original.subtracts?.length ?? 0) > 0;
    },
    enableExpanding: true,
    state: {
      globalFilter,
      columnFilters,
      expanded,
      columnOrder,
      columnPinning,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onExpandedChange: setExpanded,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onSortingChange: setSorting,
    initialState: {
      columnPinning: initialColumnPinning,
      columnOrder: initialColumnOrder,
    },
    meta: {
      reset: {
        resetCustomizations,
      },
    },
  });

  const { rows: parentRows } = table.getRowModel();

  const virtualizer = useVirtualizer({
    count: parentRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const row = parentRows[index];
      if (!row) return CELL_BASE_ROW_HEIGHT;
      const isExpanded = row.getIsExpanded();
      const originalData = row.original;
      if (isExpanded && (originalData.subtracts?.length ?? 0) > 0) {
        return (
          CELL_BASE_ROW_HEIGHT +
          CELL_MARGIN +
          CELL_HEIGHT +
          (originalData.subtracts?.length ?? 0) * CELL_HEIGHT +
          CELL_PADDING +
          CELL_MARGIN
        );
      }
      return CELL_BASE_ROW_HEIGHT;
    },
    overscan: 5,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
  });

  if (isLoading) {
    return (
      <DataTableSkeleton
        columnCount={columns.length}
        rowCount={10}
        showHeader={false}
        showToolbar={false}
      />
    );
  }

  if (error) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        title={error || 'Failed to load CRM tracts'}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <Tile>
        <EmptyState
          icon={NoDataSvg}
          headerText="No Records Found"
          helperText="Currently Insights doesn't have any available data."
        />
      </Tile>
    );
  }

  const isColumnVisible = table.getVisibleLeafColumns().length > 0;

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={(e) =>
        handleDragEnd(
          e,
          columnPinning,
          columnOrder,
          setColumnOrder,
          setColumnPinning,
          hoveredColumnIdRef
        )
      }
      modifiers={[restrictToHorizontalAxis]}
      onDragMove={onDragMove}
    >
      <TableContainer className={styles.tableContainer}>
        {showTableActions && (
          <TableToolbar>
            <TableToolbarContent>
              <TableToolbarSearch
                persistent
                placeholder="Search CRM tracts..."
                onChange={(e: any) => setGlobalFilter(e.target.value)}
                value={globalFilter}
              />
              <TableActions
                table={table}
                isColumnFilterActive={isColumnFilterActive}
                setIsColumnFilterActive={setIsColumnFilterActive}
                showExport
              />
            </TableToolbarContent>
          </TableToolbar>
        )}
        {isColumnVisible ? (
          <>
            <div className={styles.tableWrapper} ref={parentRef}>
              <Table>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Fragment key={headerGroup.id}>
                      <tr>
                        <SortableContext
                          items={columnOrder}
                          strategy={horizontalListSortingStrategy}
                        >
                          {headerGroup.headers.map((header) => (
                            <DragHeader
                              key={header.id}
                              header={header}
                              hoveredColumnIdRef={hoveredColumnIdRef}
                            />
                          ))}
                        </SortableContext>
                      </tr>
                      {isColumnFilterActive && (
                        <tr className={styles.filterRow}>
                          <ColumnFilter
                            headers={headerGroup.headers}
                            pinStyleGenerator={getPinningStyles}
                          />
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </thead>
                <tbody style={{ height: `${virtualizer.getTotalSize()}px` }}>
                  {parentRows.length === 0 ? (
                    <TableRow className={styles.emptyStateRow}>
                      <TableCell colSpan={columns.length}>
                        <EmptyState
                          icon={NoDataSvg}
                          headerText="No CRM tract data available"
                          helperText="Try adjusting your search or filters"
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    virtualizer.getVirtualItems().map((virtualRow) => {
                      const row = parentRows[virtualRow.index];
                      const isExpanded = row.getIsExpanded();
                      const originalData = row.original;
                      const visibleCells = row.getVisibleCells();
                      const lastCellIndex = visibleCells.length - 1;

                      return (
                        <tr
                          key={row.id}
                          data-index={virtualRow.index}
                          ref={(node) => virtualizer.measureElement(node)}
                          style={{
                            position: 'absolute',
                            transform: `translateY(${virtualRow.start}px)`,
                            width: '100%',
                          }}
                        >
                          {visibleCells.map((cell, cellIndex) => {
                            const isLastCell = cellIndex === lastCellIndex;
                            const widthBefore = visibleCells
                              .slice(0, cellIndex)
                              .reduce((sum, c) => sum + c.column.getSize(), 0);

                            return (
                              <td
                                key={cell.id}
                                className="bx--table-cell"
                                style={{
                                  ...getColumnStyle(cell.column.getSize()),
                                  verticalAlign: 'top',
                                  position: 'relative',
                                }}
                              >
                                <div
                                  style={{
                                    padding: '12px 0.75rem',
                                    position: 'relative',
                                  }}
                                >
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                  {isLastCell &&
                                    isExpanded &&
                                    originalData.subtracts && (
                                      <div
                                        className={styles.expandedContent}
                                        style={{
                                          position: 'absolute',
                                          left: `-${widthBefore}px`,
                                          top: '100%',
                                          marginBlock: '16px',
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <SubtractTable
                                          subtractRows={originalData.subtracts}
                                          depthRange={formatRange({
                                            start: originalData.startDepth,
                                            end: originalData.endDepth,
                                          })}
                                        />
                                      </div>
                                    )}
                                </div>
                                {isLastCell &&
                                  isExpanded &&
                                  originalData.subtracts && (
                                    <div
                                      style={{
                                        height: `${
                                          CELL_BASE_ROW_HEIGHT +
                                          CELL_MARGIN +
                                          originalData.subtracts.length *
                                            CELL_HEIGHT +
                                          CELL_PADDING +
                                          CELL_MARGIN
                                        }px`,
                                        visibility: 'hidden',
                                      }}
                                    />
                                  )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
            <div className={styles.totalRowCount}>
              <label className={styles.totalRows}>
                {DECIMAL_FORMATTER.format(
                  table.getFilteredRowModel().rows.length
                )}
              </label>
              {plur('row', table.getFilteredRowModel().rows.length)}
            </div>
          </>
        ) : (
          <HiddenColumns className={styles.hiddenColumn} />
        )}
      </TableContainer>
    </DndContext>
  );
};
